import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';

/**
 * UpdatePasswordPage · `/auth/update-password`
 *
 * Se llega acá:
 *  - desde `/auth/callback?type=invite` (primer setup de password)
 *  - desde `/auth/callback?type=recovery` (reset por olvido)
 *
 * Requiere sesión activa (vino del callback). Si no hay sesión → /login.
 *
 * Política de password: min 8 chars + al menos 1 mayúscula + 1 minúscula
 * + 1 dígito. Unificado con SecuritySection del repo hermano para evitar
 * la inconsistencia 6 vs 8 chars del marketing-hub.
 */
const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Mínimo 8 caracteres')
      .regex(/[A-Z]/, 'Debe incluir al menos una mayúscula')
      .regex(/[a-z]/, 'Debe incluir al menos una minúscula')
      .regex(/[0-9]/, 'Debe incluir al menos un número'),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    path: ['confirm'],
    message: 'Las contraseñas no coinciden',
  });

type PasswordValues = z.infer<typeof passwordSchema>;

export function UpdatePasswordPage() {
  const { session, loading } = useSupabase();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const from = params.get('from'); // 'invite' | 'recovery' | null

  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirm: '' },
  });

  if (loading) {
    return (
      <main className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-fg-muted text-body-lg">Cargando sesión…</p>
      </main>
    );
  }
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(values: PasswordValues) {
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({
      password: values.password,
    });
    setSubmitting(false);

    if (error) {
      toast.error('No se pudo actualizar la contraseña', {
        description: error.message,
      });
      return;
    }
    setDone(true);
    toast.success('Contraseña actualizada');
    setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
  }

  const eyebrow =
    from === 'invite'
      ? 'Activar cuenta · definí tu contraseña'
      : from === 'recovery'
        ? 'Recuperación · definí una nueva contraseña'
        : 'Actualizar contraseña';

  return (
    <main className="bg-background flex min-h-screen items-center justify-center p-6">
      <Card className="border-border bg-card w-full max-w-sm">
        <CardHeader className="space-y-1.5">
          <div className="flex items-center gap-2">
            <KeyRound className="text-primary h-4 w-4" />
            <CardTitle className="text-title">DenDev Core</CardTitle>
          </div>
          <p className="text-fg-muted text-body">{eyebrow}</p>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <CheckCircle2 className="text-ok h-7 w-7" />
              <p className="text-foreground text-body-lg font-semibold">
                Contraseña actualizada
              </p>
              <p className="text-fg-muted text-caption">Redirigiendo…</p>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nueva contraseña</FormLabel>
                      <FormControl>
                        <PasswordInput
                          autoComplete="new-password"
                          autoFocus
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-caption">
                        Min 8 chars · 1 mayús · 1 minús · 1 número.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar contraseña</FormLabel>
                      <FormControl>
                        <PasswordInput
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? 'Guardando…' : 'Guardar contraseña'}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
